'use client';
import { useCallback, useEffect, useState, useRef } from 'react';
import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { track } from '@repo/ui/src/services/analytics/utils';
import { recipeListFiltered, recipeListViewed } from '@repo/ui/src/services/analytics';
import CategoryGrid, { GridType } from '@components/shared/CategoryGrid';
import CategoryBody from '@components/shared/CategoryBody';
import GridItem from './_GridItem';
import GridHeader from './_GridHeader';

const RecipeCategoryBody = ({
  body,
  showBreadcrumb,
  breadcrumb,
  recipeFilters,
  _data,
  footer,
  categoryFilters,
}) => {
  const { query, path, locale } = useCurrentUrl();

  const urlFilters = {};
  if (query) {
    Object.keys(query).forEach((key) => {
      if (recipeFilters.includes(key)) {
        if (!urlFilters[key]) {
          urlFilters[key] = [query[key]];
        } else {
          urlFilters[key].push(query[key]);
        }
      }
    });
  }

  const [gridData, setGridData] = useState(
    _data ?? {
      page: 0,
      nbPages: 0,
      nbHits: 0,
      results: [],
    },
  );
  const [isLoading, setIsLoading] = useState(false);
  const [gridFilters, setGridFilters] = useState(urlFilters);
  const isFirstMount = useRef(true);

  const setFilters = async (filter) => {
    const { filterType, filterName } = filter;
    setGridFilters((filters) => {
      const _filters = { ...filters };
      if (_filters?.[filterType]?.includes(filterName)) {
        _filters[filterType] = _filters[filterType]?.filter((f) => f !== filterName);
      } else {
        if (Array.isArray(_filters[filterType])) {
          _filters[filterType].push(filterName);
        } else {
          _filters[filterType] = [filterName];
        }
      }
      return _filters;
    });
  };

  const clearFilters = () => {
    setGridFilters({});
  };

  const logListFiltered = useCallback(
    (overrideIndex = null) => {
      recipeListFiltered({
        list_id: path.replace('/recipes', ''),
        filters: gridFilters,
        recipes: gridData.hits.map((recipe, index) => ({
          recipe_id: recipe?.id,
          name: recipe?.recipeMetaSection?.title,
          position: overrideIndex ? overrideIndex + index : index + 1,
          url: `${window.location.protocol}://${window.location.hostname}/${recipe?._meta?.deliveryKey}`,
          image_url: recipe?.assets?.[0]?.url,
        })),
      });
    },
    [path, gridData, gridFilters],
  );

  const logListViewed = useCallback(
    (overrideIndex = null) => {
      track(recipeListViewed, {
        list_id: path.replace('/recipes', ''),
        recipes: gridData.hits?.map((recipe, index) => ({
          recipe_id: recipe?.id,
          name: recipe?.recipeMetaSection?.title,
          position: overrideIndex ? overrideIndex + index + 1 : index + 1,
          url: `${window.location.protocol}://${window.location.hostname}/${recipe?._meta?.deliveryKey}`,
          image_url: recipe?.assets?.[0]?.url,
        })),
      });
    },
    [path, gridData],
  );

  const loadData = useCallback(
    async ({ page }, controller, startIndex = 0) => {
      const res = await axios.post(
        '/api/recipe-category/getRecipeCategory',
        {
          categories: recipeFilters,
          page,
          filters: gridFilters,
          lang: locale,
        },
        { signal: controller.signal },
      );
      if (startIndex) {
        if (!isEmpty(gridFilters)) {
          logListFiltered(startIndex);
        } else {
          logListViewed(startIndex);
        }
      }

      return res;
    },
    [gridFilters, recipeFilters, locale, logListViewed, logListFiltered],
  );

  useEffect(() => {
    const controller = new AbortController();

    const reload = async () => {
      setIsLoading(true);
      const res = await loadData({ page: 0, _filters: gridFilters }, controller);
      setGridData(res?.data ?? {});
      setIsLoading(false);
    };

    if (isFirstMount.current) {
      isFirstMount.current = false;
    } else {
      reload();
      if (!isEmpty(gridFilters)) {
        logListFiltered();
      } else {
        logListViewed();
      }
    }
    return () => controller.abort();
  }, [gridFilters]);

  return (
    <CategoryBody
      breadcrumb={breadcrumb}
      showBreadcrumb={showBreadcrumb}
      body={body}
      footer={footer}
    >
      <GridHeader
        filters={categoryFilters}
        currentFilters={gridFilters}
        setFilters={setFilters}
        nbHits={gridData.nbHits}
        clearFilters={clearFilters}
      />
      <CategoryGrid
        isLoading={isLoading}
        _data={gridData}
        loadData={loadData}
        GridItemComponent={GridItem}
        type={GridType.Recipe}
        hasFilters={!isEmpty(gridFilters)}
      />
    </CategoryBody>
  );
};

export default RecipeCategoryBody;
